import { API_BASE, ApiError, getToken, privateDocumentBlob, req } from "./transport";
import { profileFetch } from "../profileSession";

export type WorkTarget = { kind: "contract" | "engagement"; id: string };
export type WorkImage = { id: string; reportId: string; position: number; contentType: string; sizeBytes: number; sha256: string; createdAt: string };
export type WorkScopedEntry = {
  id: string; reportId: string; kind: "comment" | "correction"; text: string; createdAt: string;
  authorUserId: number; authorCompanyId: string | null; authorName: string | null; authorRole: string; images: (WorkImage & { scopedEntryId: string })[];
};
export type WorkReport = {
  id: string; contractId: string | null; engagementId: string | null; text: string; createdAt: string;
  authorUserId: number; authorCompanyId: string | null; authorName: string | null; authorRole: string; images: WorkImage[];
  scopedEntries: WorkScopedEntry[];
};
export type WorkReportFeed = { reports: WorkReport[]; canWrite: boolean; readOnlyReason: string | null };
export type WorkReportCommand = { commandId: string; text: string; images: File[] };
export type WorkScopedCommand = WorkReportCommand & { kind: "comment" | "correction" };
const path = (target: WorkTarget) => `/${target.kind === "contract" ? "contracts" : "engagements"}/${encodeURIComponent(target.id)}/work`;
async function postEntry<T>(url: string, command: WorkReportCommand | WorkScopedCommand): Promise<T> {
  const form = new FormData(); form.append("commandId", command.commandId); form.append("text", command.text);
  if ("kind" in command) form.append("kind", command.kind);
  for (const image of command.images) form.append("images", image);
  const token = getToken();
  const response = await profileFetch(`${API_BASE}${url}`, { method: "POST", body: form, credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {} });
  const body = await response.json();
  if (!response.ok) throw new ApiError(response.status, typeof body?.error === "string" ? body.error : "Could not confirm this entry. Retry the same entry.", typeof body?.code === "string" ? body.code : undefined);
  return body;
}
export const workReportsApi = {
  workReports(target: WorkTarget) { return req<WorkReportFeed>(`${path(target)}/reports`, {}, { rejectUnauthorized: true }); },
  postWorkReport(target: WorkTarget, command: WorkReportCommand) {
    return postEntry<{ report: WorkReport; replayed: boolean }>(`${path(target)}/reports`, command);
  },
  postWorkScopedEntry(target: WorkTarget, reportId: string, command: WorkScopedCommand) {
    return postEntry<{ entry: WorkScopedEntry; replayed: boolean }>(`${path(target)}/reports/${encodeURIComponent(reportId)}/entries`, command);
  },
  workImage(target: WorkTarget, imageId: string, signal?: AbortSignal) { return privateDocumentBlob(`${path(target)}/images/${encodeURIComponent(imageId)}`, signal); },
};
